import { API } from "aws-amplify";
import { listServiceRequests } from "~/graphql/queries";
import { getGraphqlFilters } from "~/utils";
import {
  FilterValue,
  GRAPHQLFilters,
  LoaderData,
  ServiceRequestListFilters,
} from "~/types";
import * as APITypes from "~/API";

export const getServiceRequestList = async ({
  ownerID,
  ownerType,
  tenantID,
  entityID,
  entityType,
  filters,
  createdAt,
  includeDeleted = "exclude",
}: {
  ownerID?: string;
  ownerType?: APITypes.OwnerType;
  tenantID?: string;
  entityID?: string;
  entityType?: APITypes.EntityType;
  filters?: ServiceRequestListFilters;
  createdAt?: FilterValue;
  includeDeleted?: "all" | "exclude" | "only";
}): Promise<LoaderData<APITypes.ServiceRequest[]>> => {
  let graphqlFilters: GRAPHQLFilters & Record<string, any> = getGraphqlFilters({
    tenantID,
    ownerID,
    ownerType,
    entityType,
    entityID,
    status: filters?.status,
    type: filters?.type,
    createdAt,
  });

  // Add text-based filters with contains operator
  if (filters?.title) {
    graphqlFilters.title = { contains: filters.title };
  }
  if (filters?.handlerName) {
    graphqlFilters.handlerName = { contains: filters.handlerName };
  }
  if (filters?.tenantName) {
    graphqlFilters.tenantName = { contains: filters.tenantName };
  }
  if (filters?.contractor) {
    graphqlFilters.contractor = { contains: filters.contractor };
  }

  // Add date range filters for createdAt
  // ISO date strings are lexicographically sortable, so string comparison works
  // Note: Date range filters take precedence over the createdAt parameter
  if (filters?.createdAtIntervalStart || filters?.createdAtIntervalEnd) {
    // If both dates are provided, use between filter
    if (filters.createdAtIntervalStart && filters.createdAtIntervalEnd) {
      // Convert date strings to ISO format
      // Start date: beginning of day (00:00:00.000Z)
      const startDate = new Date(filters.createdAtIntervalStart);
      startDate.setHours(0, 0, 0, 0);
      const startISO = startDate.toISOString();

      // End date: end of day (23:59:59.999Z)
      const endDate = new Date(filters.createdAtIntervalEnd);
      endDate.setHours(23, 59, 59, 999);
      const endISO = endDate.toISOString();

      // Replace entire createdAt filter with between filter
      // (date range filters take precedence over createdAt parameter)
      graphqlFilters.createdAt = { between: [startISO, endISO] } as any;
    } else {
      // Only one date provided, use ge (>=) or le (<=)
      // Note: GraphQL ModelStringInput uses 'ge' and 'le', not 'gte' and 'lte'
      // Replace entire createdAt filter to avoid mixing operators
      const createdAtFilter: any = {};

      if (filters.createdAtIntervalStart) {
        // Convert date string to ISO format and set start of day
        const startDate = new Date(filters.createdAtIntervalStart);
        startDate.setHours(0, 0, 0, 0);
        createdAtFilter.ge = startDate.toISOString();
      }
      if (filters.createdAtIntervalEnd) {
        // Convert date string to ISO format and set end of day
        const endDate = new Date(filters.createdAtIntervalEnd);
        endDate.setHours(23, 59, 59, 999);
        createdAtFilter.le = endDate.toISOString();
      }

      graphqlFilters.createdAt = createdAtFilter;
    }
  }

  // Add _deleted filter based on includeDeleted parameter
  if (includeDeleted === "exclude") {
    graphqlFilters._deleted = { ne: true };
  } else if (includeDeleted === "only") {
    graphqlFilters._deleted = { eq: true };
  }
  // If includeDeleted === "all", don't add any _deleted filter

  let items: APITypes.ServiceRequest[] = [];
  let nextToken: string | null | undefined = undefined;
  try {
    do {
      let response = (await API.graphql({
        query: listServiceRequests,
        variables: {
          filter: graphqlFilters,
          nextToken,
        },
      })) as { data?: APITypes.ListServiceRequestsQuery };

      const allServiceRequests =
        response.data?.listServiceRequests?.items?.filter(
          (item): item is APITypes.ServiceRequest => item !== null
        ) ?? [];
      items.push(...allServiceRequests);
      nextToken = response.data?.listServiceRequests?.nextToken;
    } while (nextToken);

    return {
      success: true,
      data: items,
    };
  } catch (error) {
    return {
      success: false,
      data: [],
      error,
    };
  }
};
